import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Search, UserRound, Users, Trash2, Loader2, Pencil, UserPlus, Merge } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import SegmentedControl from "../components/SegmentedControl";
import CreateCharacterDialog from "../components/CreateCharacterDialog";
import EmptyScriptOnboarding from "../components/EmptyScriptOnboarding";
import CharacterMergeDialog from "../components/CharacterMergeDialog";
import CastAliasSuggestions from "../components/CastAliasSuggestions";
import PossibleCharactersSection from "../components/PossibleCharactersSection";
import EpisodeFilter from "../components/EpisodeFilter";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";

const LENS_KEY = "onsetflow:cast-lens";
const TEAM_KEY = "onsetflow:cast-team";
const SORT_KEY = "onsetflow:cast-sort";
// Iter-101 — per-project episode selection. Persisting it as a
// localStorage pref keyed by projectId would be noisier than useful
// (users frequently hop between episodes), so we keep it in state only
// and default to "All".

const SORT_OPTIONS = ["number", "name"];
const SORT_LABELS = {
    number: "By number",
    name: "By name",
};

function sortCharacterList(list, sortBy) {
    const copy = [...list];
    if (sortBy === "name") {
        copy.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, {
                sensitivity: "base",
            }),
        );
        return copy;
    }
    if (sortBy === "number") {
        copy.sort((a, b) => {
            const an = (a.character_number || "").trim();
            const bn = (b.character_number || "").trim();
            // Empty numbers always sort to the end so users see the
            // "still unnumbered" tail instead of having them jumble in
            // with the numbered list.
            if (!an && !bn) return 0;
            if (!an) return 1;
            if (!bn) return -1;
            const ai = parseInt(an, 10);
            const bi = parseInt(bn, 10);
            if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) {
                // True numeric sort: 1, 2, 3, 10 — not 1, 10, 2, 3.
                return ai - bi;
            }
            return an.localeCompare(bn, undefined, { numeric: true });
        });
        return copy;
    }
    // Default fallback when sortBy is unrecognised: name sort.
    copy.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
            sensitivity: "base",
        }),
    );
    return copy;
}

const LENS_LABELS = {
    prep: "Fittings",
    onset: "On Set",
};

function readPref(key, allowed, fallback) {
    try {
        const v = localStorage.getItem(key);
        if (allowed.includes(v)) return v;
    } catch { /* localStorage may be unavailable */ }
    return fallback;
}

function savePref(key, v) {
    try {
        localStorage.setItem(key, v);
    } catch { /* ignore */ }
}

/**
 * Neutral "Cast" landing for a project. Lens toggle (Fittings | On Set)
 * controls labelling and click destination — NOT the underlying records.
 *
 * The On-Set lens exposes a sub-filter (All / Main Team / Crowd Team) that
 * is purely a viewing filter on the SAME records. Each character has a
 * single `team` field on the backend; tapping the chip on a card flips it.
 */
export default function CastHub() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();

    const [project, setProject] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Iter-120 — explicit "cast load failed" state so the page can
    // render a Retry affordance instead of a silent empty grid.
    const [castError, setCastError] = useState("");
    const [retryKey, setRetryKey] = useState(0);
    const [q, setQ] = useState("");
    // Inbound state from On Set search ({lens, team, focusCharacter}) wins
    // over the persisted preference so the user is dropped on the exact
    // filter slice they came from. Persisted prefs are still saved on
    // subsequent toggles.
    const [lens, setLens] = useState(() => {
        const incoming = location.state?.lens;
        if (incoming === "prep" || incoming === "onset") return incoming;
        // iter-47: Default to On Set (primary surface) instead of Fittings.
        return readPref(LENS_KEY, ["prep", "onset"], "onset");
    });
    const [team, setTeam] = useState(() => {
        const incoming = location.state?.team;
        if (incoming === "all" || incoming === "main" || incoming === "crowd") {
            return incoming;
        }
        return readPref(TEAM_KEY, ["all", "main", "crowd"], "all");
    });
    const focusCharacterId = location.state?.focusCharacter || null;
    const focusRef = useRef(null);
    // Delete-confirmation state. We pre-fetch usage counts so the user sees
    // exactly how many photos will move to Unassigned BEFORE committing.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteUsage, setDeleteUsage] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Edit-name dialog state. Updates are partial PATCHes — the existing
    // character_id is preserved so every reference (scene attachments,
    // state entries, flags) automatically picks up the new name.
    const [editTarget, setEditTarget] = useState(null);
    const [editName, setEditName] = useState("");
    const [editActor, setEditActor] = useState("");
    const [editError, setEditError] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [editNumber, setEditNumber] = useState("");
    const [editTeam, setEditTeam] = useState("main");

    // Create-character dialog. Defaults to team='crowd' per spec — most
    // manual additions are crowd entries; a one-tap toggle promotes to Main.
    const [createOpen, setCreateOpen] = useState(false);
    // iter-103 PART 2 + 3 — duplicate detection + manual merge dialog.
    const [mergeOpen, setMergeOpen] = useState(false);
    // Iter-132 — preset values for the merge dialog when launched from
    // the Possible aliases card. Resets to null when the dialog closes.
    const [mergePreset, setMergePreset] = useState(null);

    // iter-57: sort selector. Default `null` → resolved on first render
    // by checking if any character has a `character_number` (then prefer
    // "number"). Persisted between visits so a fresh-numbered cast
    // doesn't jump back to import order.
    const [sortBy, setSortBy] = useState(() =>
        readPref(SORT_KEY, SORT_OPTIONS, null),
    );

    // Effective permissions for the current user. Owners always pass;
    // Iter-83 — full per-cap permissions for the requesting user.
    // `permissions/me` returns all 6 fine-grained caps + the legacy
    // `can_create_characters`. We surface helpers for the three Cast
    // caps so the UI can hide buttons when the cap isn't granted.
    const [permissions, setPermissions] = useState({});
    // Iter-148 — Possible Characters review layer.
    const [possibleRows, setPossibleRows] = useState([]);
    // Iter-101 — episode awareness. `episodes` = every episode that
    // exists on this project (from /episodes endpoint); `episode` =
    // currently-selected filter (null → All).
    const [episodes, setEpisodes] = useState([]);
    const [episode, setEpisode] = useState(null);
    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    // Source of truth is /permissions/me which already forces every
    // cap to false when archived. We additively gate the
    // owner/admin shortcut paths so write affordances also hide for
    // privileged roles on archived projects.
    const isArchived = Boolean(project?.archived);
    const canCreateCharacters =
        !isArchived &&
        (isOwner ||
            isAdmin ||
            Boolean(permissions?.cast_add) ||
            Boolean(permissions?.can_create_characters));
    const canEditCast =
        !isArchived &&
        (isOwner || isAdmin || Boolean(permissions?.cast_edit));
    const canDeleteCast =
        !isArchived &&
        (isOwner || isAdmin || Boolean(permissions?.cast_delete));
    // Iter-106 — Bulk-edit Cast affordance must be reachable by anyone
    // who can actually edit cast (owner, admin, or any member with the
    // `cast_edit` cap). Previously gated owner-only, so admins / leads
    // who'd been promoted to admin lost access to the bulk numbering /
    // descriptions pass — a critical mobile + tablet feature-parity bug
    // surfaced by the user.
    const canBulkEditCast = canEditCast;

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const [{ data: p }, { data: perms }, { data: eps }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`, { signal: ctrl.signal }),
                    api.get(`${apiBase}/${projectId}/permissions/me`, { signal: ctrl.signal }),
                    api
                        .get(`${apiBase}/${projectId}/episodes`, { signal: ctrl.signal })
                        .catch(() => ({ data: { episodes: [] } })),
                ]);
                setProject(p);
                setPermissions(perms?.permissions || {});
                setEpisodes(eps?.episodes || []);
            } catch (e) {
                if (ctrl.signal.aborted) return;
                setError(formatApiError(e));
            }
        })();
        return () => ctrl.abort();
    }, [projectId, apiBase]);

    // Iter-101 — re-fetch cast whenever the episode filter changes.
    // Iter-120 — search is ALWAYS project-wide. When the user types
    // a query we drop the episode filter so "Felix" returns Felix
    // across every episode, not just the active one. The episode
    // filter still applies when search is empty.
    // Iter-120 — automatic 1-attempt retry on network/5xx so a single
    // hiccup never produces a false-empty Cast page. Persistent failure
    // surfaces a Retry button via `castError`.
    const trimmedQ = q.trim();
    useEffect(() => {
        const ctrl = new AbortController();
        let attempts = 0;
        let cancelled = false;
        const fetchOnce = async () => {
            attempts += 1;
            const params = {};
            if (!trimmedQ && episode) params.episode = episode;
            if (trimmedQ) params.q = trimmedQ;
            try {
                const { data: ch } = await api.get(
                    `${apiBase}/${projectId}/characters`,
                    { params, signal: ctrl.signal },
                );
                if (cancelled) return;
                setCharacters(Array.isArray(ch) ? ch : []);
                setCastError("");
            } catch (e) {
                if (ctrl.signal.aborted || cancelled) return;
                // Auto-retry once for transient errors (network or 5xx).
                const status = e?.response?.status;
                const transient = !status || status >= 500;
                if (transient && attempts < 2) {
                    await new Promise((r) => setTimeout(r, 600));
                    if (!cancelled) await fetchOnce();
                    return;
                }
                setCastError(formatApiError(e) || "Unable to load cast");
            } finally {
                if (!ctrl.signal.aborted && !cancelled) setLoading(false);
            }
        };
        setLoading(true);
        setCastError("");
        fetchOnce();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [projectId, episode, trimmedQ, retryKey, apiBase]);

    // Iter-148 — Possible Characters review layer fetch + refresh hook.
    const refetchPossibles = async () => {
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/possible-characters`,
                { params: { status: "pending" } },
            );
            setPossibleRows(Array.isArray(data) ? data : []);
        } catch {
            /* silent — section just hides if list fails */
        }
    };
    useEffect(() => {
        if (!projectId) return;
        refetchPossibles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, retryKey]);

    // Scroll the focused character into view when this page is opened
    // from the On Set search "Cast" results. Keeps the visual link
    // between the search match and the character card.
    useEffect(() => {
        if (!focusCharacterId || loading) return;
        const el = focusRef.current;
        if (!el) return;
        try {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_e) {
            /* ignore */
        }
    }, [focusCharacterId, loading]);

    const hasAnyNumber = useMemo(
        () => characters.some((c) => (c.character_number || "").trim() !== ""),
        [characters],
    );
    // Effective sort: explicit user choice wins, otherwise prefer
    // "number" when any character has been numbered (so a freshly
    // bulk-numbered cast feels production-ready immediately).
    const effectiveSort = sortBy || (hasAnyNumber ? "number" : "name");

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        let list = characters;
        // Sub-filter by team applies ONLY in the On Set lens (per spec).
        if (lens === "onset" && team !== "all") {
            list = list.filter((c) => (c.team || "main") === team);
        }
        if (term) {
            list = list.filter((c) =>
                [c.name, c.actor, c.character_number]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(term),
            );
        }
        return sortCharacterList(list, effectiveSort);
    }, [q, characters, lens, team, effectiveSort]);

    const switchSort = (next) => {
        setSortBy(next);
        savePref(SORT_KEY, next);
    };

    const switchLens = (next) => {
        setLens(next);
        savePref(LENS_KEY, next);
    };

    const switchTeam = (next) => {
        setTeam(next);
        savePref(TEAM_KEY, next);
    };

    const openDelete = async (e, character) => {
        // Owner-only entry point. Opens the confirmation dialog and fetches
        // usage counts in parallel so the user sees the impact before
        // committing.
        e.preventDefault();
        e.stopPropagation();
        setDeleteTarget(character);
        setDeleteUsage(null);
        setDeleteLoading(true);
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/characters/${character.id}/usage`
            );
            setDeleteUsage(data);
        } catch (err) {
            setError(formatApiError(err));
            setDeleteTarget(null);
        } finally {
            setDeleteLoading(false);
        }
    };

    const cancelDelete = () => {
        if (deleting) return;
        setDeleteTarget(null);
        setDeleteUsage(null);
    };

    const openEdit = (e, character) => {
        e.preventDefault();
        e.stopPropagation();
        setEditTarget(character);
        setEditName(character.name || "");
        setEditActor(character.actor || "");
        setEditNumber(character.character_number || "");
        setEditTeam(character.team || "main");
        setEditError("");
    };

    const cancelEdit = () => {
        if (savingEdit) return;
        setEditTarget(null);
        setEditError("");
    };

    const saveEdit = async () => {
        if (!editTarget) return;
        const name = editName.trim();
        if (!name) {
            setEditError("Name cannot be blank.");
            return;
        }
        setSavingEdit(true);
        setEditError("");
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/characters/${editTarget.id}`,
                {
                    name,
                    actor: editActor.trim() || null,
                    character_number: editNumber.trim() || null,
                    team: editTeam,
                }
            );
            setCharacters((list) =>
                list.map((c) => (c.id === data.id ? { ...c, ...data } : c))
            );
            toast.success(`Updated ${data.name}`);
            setEditTarget(null);
        } catch (err) {
            setEditError(formatApiError(err));
        } finally {
            setSavingEdit(false);
        }
    };

    const openCreate = () => setCreateOpen(true);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const { data } = await api.delete(
                `${apiBase}/${projectId}/characters/${deleteTarget.id}`
            );
            // Optimistically remove from the list.
            setCharacters((list) => list.filter((c) => c.id !== deleteTarget.id));
            const moved = data?.unlinked_entries || 0;
            toast.success(
                moved
                    ? `${deleteTarget.name} deleted · ${moved} image${moved === 1 ? "" : "s"} moved to Unassigned`
                    : `${deleteTarget.name} deleted`
            );
            setDeleteTarget(null);
            setDeleteUsage(null);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    const counts = useMemo(() => {
        const out = { all: characters.length, main: 0, crowd: 0 };
        for (const c of characters) {
            const t = c.team || "main";
            if (t === "main") out.main += 1;
            else if (t === "crowd") out.crowd += 1;
        }
        return out;    }, [characters]);

    // iter-63 PART 1: contextual subtitle ("On Set — Cast" / "Fittings — Cast").
    const ctxSubtitle = lens ? `${LENS_LABELS[lens]} — Cast` : "Cast";

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <ProjectHeader projectName={project?.name} subtitle={ctxSubtitle} />
                <main className="flex-1 grid place-items-center text-neutral-500">
                    Loading cast…
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <ProjectHeader projectName={project?.name} subtitle={ctxSubtitle} />
            <main className="max-w-2xl mx-auto px-5 pt-5 pb-48 w-full">
                <div className="mb-4 flex items-start gap-3">
                    <div
                        data-testid="cast-hub-identity-icon"
                        className="h-10 w-10 rounded-lg bg-secondary border border-white/10 text-neutral-200 flex items-center justify-center shrink-0"
                    >
                        <UserRound size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                            {project?.name || "Project"}
                        </div>
                        <h1
                            data-testid="cast-hub-title"
                            className="font-display text-4xl font-black text-white mt-1"
                        >
                            Cast
                        </h1>
                    </div>
                </div>

                {/* iter-47: Lens toggle — On Set | Fittings (On Set primary, leftmost). */}
                <SegmentedControl
                    testid="cast-hub-lens-toggle"
                    ariaLabel="Cast lens"
                    value={lens}
                    onChange={switchLens}
                    options={[
                        { value: "onset", label: LENS_LABELS.onset, testid: "cast-hub-lens-onset" },
                        { value: "prep", label: LENS_LABELS.prep, testid: "cast-hub-lens-prep" },
                    ]}
                    className="mb-3"
                />

                {/* Iter-152 — Possible Characters Review surface was
                    previously here (above search). Moved BELOW the
                    Possible Aliases card so the user can resolve
                    alias collisions FIRST, which often shrinks the
                    Cast Review list before they touch it. */}

                {/* iter-77 PART 6 — removed "Open {lens} mode" CTA. */}

                {/* iter-77 PART 5/7 — unified cast layout order:
                    1. Mode toggle  (lens, above)
                    2. Search       (primary action)
                    3. Filters      (team — All / Main / Crowd)
                    4. Sort         (low emphasis)
                    5. Results      (below)
                    6. Secondary actions (Add, Bulk edit — below results)
                    Search is the strongest visual element; filters and
                    sort are progressively de-emphasised; secondary
                    curation actions sit at the bottom so the cast
                    list is the user's first impression of the page. */}
                <div className="mb-3">
                    <div className="relative">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                        />
                        <Input
                            data-testid="cast-hub-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search cast (name or number)"
                            className="pl-8 h-12 bg-card border-white/10 text-white placeholder:text-neutral-500"
                        />
                    </div>
                </div>

                {/* iter-101 — episode filter (TV projects only). Sits
                    directly above the team filter so the user reads:
                    "which episode → which team → which cast". */}
                <EpisodeFilter
                    testid="cast-episode-filter"
                    episodes={episodes}
                    value={episode}
                    onChange={setEpisode}
                />

                {/* iter-77 PART 3 — Cast filters always visible. */}
                <SegmentedControl
                    testid="cast-hub-team-filter"
                    ariaLabel="Team filter"
                    variant="pills"
                    value={team}
                    onChange={switchTeam}
                    options={[
                        { value: "all", label: "All", count: counts.all, testid: "cast-hub-team-all" },
                        { value: "main", label: "Main Team", count: counts.main, testid: "cast-hub-team-main" },
                        { value: "crowd", label: "Crowd", count: counts.crowd, testid: "cast-hub-team-crowd" },
                    ]}
                    className="mb-3"
                />

                {/* iter-77 PART 6 — removed "Open {lens} mode" CTA. */}

                {error ? (
                    <div className="rounded-md border border-red-500/30 bg-red-500/10 text-red-200 text-sm p-3 mb-4">
                        {error}
                    </div>
                ) : null}

                {/* iter-57: sort selector. Only renders once the project
                    has any cast — for empty projects there's nothing to
                    sort. Defaults to "By number" once any character is
                    numbered, otherwise "Import order". User choice is
                    persisted in localStorage. */}
                {characters.length > 1 ? (
                    <div
                        data-testid="cast-hub-sort-row"
                        className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                    >
                        <span className="shrink-0">Sort</span>
                        <div className="flex items-center rounded-md bg-secondary border border-white/10 overflow-hidden">
                            {SORT_OPTIONS.map((k, i) => (
                                <button
                                    key={k}
                                    type="button"
                                    aria-pressed={effectiveSort === k}
                                    data-active={effectiveSort === k ? "true" : "false"}
                                    data-testid={`cast-hub-sort-${k}`}
                                    onClick={() => switchSort(k)}
                                    className={[
                                        "tap h-8 px-2.5 text-[10px] font-semibold uppercase tracking-wider transition-colors",
                                        effectiveSort === k
                                            ? "bg-primary text-primary-foreground ring-1 ring-primary"
                                            : "bg-transparent text-neutral-300",
                                        i > 0 ? "border-l border-white/10" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                >
                                    {SORT_LABELS[k]}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* iter-79 PART 2 / iter-106 — Bulk edit cast must be VISIBLE
                    and ALWAYS reachable for anyone with cast-edit
                    permission. Sits BELOW the filters/sort row and ABOVE
                    the cast list (and the empty / no-match states) on
                    every cast view — Fittings + On Set lens. Owner +
                    admin + members with `cast_edit` cap. Stays visible
                    even when the cast list is empty so leads can use it
                    as the initial setup surface (CSV import, numbering,
                    etc.).
                    Iter-130a — Merge cast moved UP to sit alongside
                    Bulk edit. The bottom Merge button (previously
                    below the cast list) was easy to miss; scripts
                    routinely contain alias variants ("Younger Felix",
                    "Ghost Felix", "Felix Screams") and the merge tool
                    needs to be the first thing a lead sees while
                    curating the cast. */}
                {/* Iter-132 — Possible aliases card. Renders only when
                    the parser surfaces likely identity relationships
                    (modifier_prefix, crowd_index). Calm, dismissable,
                    and non-destructive — clicking Review opens the
                    existing merge dialog with the pair pre-selected. */}
                {canCreateCharacters && characters.length > 0 ? (
                    <CastAliasSuggestions
                        projectId={projectId}
                        refreshKey={retryKey}
                        onMergeRequest={(preset) => {
                            setMergePreset(preset);
                            setMergeOpen(true);
                        }}
                    />
                ) : null}
                {/* Iter-152 — Cast Review (was "Needs Review" until iter-151).
                    Sits BELOW Possible Aliases so alias resolution happens
                    first, which often shrinks the Cast Review list before
                    the user touches it. */}
                {possibleRows.length > 0 ? (
                    <div className="mb-3">
                        <PossibleCharactersSection
                            projectId={projectId}
                            rows={possibleRows}
                            characters={characters}
                            canManage={canCreateCharacters}
                            defaultOpen={Boolean(
                                location.state?.openNeedsReview,
                            ) || possibleRows.length > 0}
                            onMutated={async () => {
                                await refetchPossibles();
                                setRetryKey((k) => k + 1);
                            }}
                        />
                    </div>
                ) : null}
                {canBulkEditCast || canCreateCharacters ? (
                    <div
                        data-testid="cast-hub-curate-row"
                        className="mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2"
                    >
                        {canCreateCharacters ? (
                            <button
                                type="button"
                                data-testid="cast-hub-add-btn-top"
                                onClick={openCreate}
                                title="Add a character (Crowd, dailies, anyone we missed)"
                                className="tap inline-flex items-center justify-center gap-2 h-10 w-full rounded-md border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-sm font-semibold text-emerald-200"
                            >
                                <UserPlus size={14} />
                                Add character
                            </button>
                        ) : null}
                        {canBulkEditCast ? (
                            <Link
                                data-testid="cast-hub-bulk-edit-link"
                                to={`${routeBase}/${projectId}/cast/bulk`}
                                className="tap inline-flex items-center justify-center gap-2 h-10 w-full rounded-md border border-primary/40 bg-primary/10 hover:bg-primary/20 text-sm font-semibold text-primary"
                            >
                                <Pencil size={14} />
                                Bulk edit cast
                            </Link>
                        ) : null}
                        {canCreateCharacters ? (
                            <button
                                type="button"
                                data-testid="cast-hub-merge-btn-top"
                                onClick={() => setMergeOpen(true)}
                                title="Merge duplicate characters (e.g. 'Felix' and 'Younger Felix' into one)"
                                className="tap inline-flex items-center justify-center gap-2 h-10 w-full rounded-md border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/15 text-sm font-semibold text-amber-200"
                            >
                                <Merge size={14} />
                                Merge cast
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {castError ? (
                    <div
                        data-testid="cast-hub-error"
                        className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 text-center text-sm space-y-3"
                    >
                        <p className="text-red-300 font-semibold">
                            Unable to load cast
                        </p>
                        <p className="text-neutral-400 text-xs">
                            {castError}
                        </p>
                        <button
                            type="button"
                            data-testid="cast-hub-retry"
                            onClick={() => {
                                setCastError("");
                                setRetryKey((k) => k + 1);
                            }}
                            className="instant-tap inline-flex items-center justify-center h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-semibold"
                        >
                            Retry
                        </button>
                    </div>
                ) : characters.length === 0 ? (
                    canCreateCharacters ? (
                        <EmptyScriptOnboarding
                            projectId={projectId}
                            variant="cast"
                            role={project?.role}
                            canManageScript={project?.can_manage_script}
                            secondaryAction={{
                                label: "Add cast manually",
                                onClick: openCreate,
                            }}
                        />
                    ) : (
                        <div
                            data-testid="cast-hub-empty"
                            className="rounded-xl border border-dashed border-white/10 bg-card p-6 text-center text-neutral-400 text-sm"
                        >
                            No cast yet. Ask the project owner to add cast or
                            grant you permission.
                        </div>
                    )
                ) : filtered.length === 0 ? (
                    <div
                        data-testid="cast-hub-no-match"
                        className="rounded-xl border border-dashed border-white/10 bg-card p-6 text-center text-neutral-400 text-sm"
                    >
                        No cast matches your filter.
                    </div>
                ) : (
                    <ul data-testid="cast-hub-list" className="space-y-2">
                        {filtered.map((c) => {
                            const t = c.team || "main";
                            const isFocused = c.id === focusCharacterId;
                            return (
                                <li
                                    key={c.id}
                                    ref={isFocused ? focusRef : null}
                                >
                                    <Link
                                        data-testid={`cast-hub-character-${c.id}`}
                                        to={`${routeBase}/${projectId}/characters/${c.id}`}
                                        state={{
                                            lens,
                                            returnTo: {
                                                path: `${routeBase}/${projectId}/cast`,
                                                label: "Back to Cast",
                                            },
                                        }}
                                        className={`tap flex items-center gap-3 rounded-xl border bg-card p-3 hover:bg-white/5 transition-colors ${
                                            isFocused
                                                ? "border-primary/60 ring-1 ring-primary/40"
                                                : "border-white/10"
                                        }`}
                                    >
                                        <div
                                            data-testid={`cast-hub-avatar-${c.id}`}
                                            className={[
                                                "h-12 w-12 shrink-0 rounded-full font-display font-bold flex items-center justify-center",
                                                c.character_number
                                                    ? "bg-primary text-primary-foreground text-base mono"
                                                    : "bg-secondary text-white text-lg",
                                            ].join(" ")}
                                            title={
                                                c.character_number
                                                    ? `Character #${c.character_number}`
                                                    : c.name
                                            }
                                        >
                                            {/* iter-57: show the assigned
                                                character number in the
                                                avatar circle once one
                                                exists; fall back to the
                                                first letter for unnumbered
                                                cast so "still-to-number"
                                                rows are obvious at a
                                                glance. */}
                                            {c.character_number ||
                                                c.name.slice(0, 1).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-medium truncate">
                                                {c.name}
                                            </div>
                                            {c.actor ? (
                                                <div className="text-xs text-neutral-400 truncate">
                                                    {c.actor}
                                                </div>
                                            ) : null}
                                            {/* iter-101 — episode chips.
                                                Surfaced on every card so
                                                users see which episodes
                                                a character appears in
                                                regardless of the current
                                                filter. Hidden when the
                                                project has no episodes. */}
                                            {Array.isArray(c.episodes) && c.episodes.length > 0 ? (
                                                <div
                                                    data-testid={`cast-hub-episodes-${c.id}`}
                                                    className="mt-1 flex flex-wrap gap-1"
                                                >
                                                    {c.episodes.map((ep) => (
                                                        <span
                                                            key={ep}
                                                            className="inline-flex items-center h-5 px-1.5 rounded-full text-[9px] uppercase tracking-[0.15em] bg-white/5 border border-white/10 text-neutral-300"
                                                        >
                                                            Ep {ep}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                        {/* Team chip is now PASSIVE for
                                            everyone. Previously this was an
                                            interactive button on owner cards
                                            that flipped Main↔Crowd inline —
                                            taps near the chip silently
                                            re-classified the character and
                                            (when the user was filtering by
                                            team) popped them out of the
                                            visible list, looking exactly
                                            like an accidental delete. Team
                                            switching now lives in the Edit
                                            dialog (iter 37) where it
                                            belongs. */}
                                        <span
                                            data-testid={`cast-hub-team-badge-${c.id}`}
                                            className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] border ${
                                                t === "main"
                                                    ? "bg-primary/15 border-primary/40 text-primary"
                                                    : "bg-amber-500/10 border-amber-500/30 text-amber-200"
                                            }`}
                                        >
                                            {t === "main" ? (
                                                <UserRound size={11} />
                                            ) : (
                                                <Users size={11} />
                                            )}
                                            {t === "main" ? "Main" : "Crowd"}
                                        </span>
                                        {canEditCast ? (
                                            <button
                                                type="button"
                                                data-testid={`cast-hub-edit-${c.id}`}
                                                onClick={(e) => openEdit(e, c)}
                                                title="Edit name"
                                                aria-label={`Edit ${c.name}`}
                                                className="tap shrink-0 h-9 w-9 rounded-md text-neutral-400 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                        ) : null}
                                        {canDeleteCast ? (
                                            <button
                                                type="button"
                                                data-testid={`cast-hub-delete-${c.id}`}
                                                onClick={(e) => openDelete(e, c)}
                                                title="Delete cast member"
                                                aria-label={`Delete ${c.name}`}
                                                className="tap shrink-0 h-9 w-9 rounded-md text-neutral-500 hover:text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        ) : null}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {/* iter-79 PART 2 — Add character button (bulk edit
                    moved ABOVE the cast list).
                    Iter-130a — Merge cast was duplicated here; the
                    canonical entry now lives at the top alongside
                    Bulk edit. This row is just Add character so the
                    "+" affordance stays at the bottom of the list
                    where users naturally tab to it. */}
                {/* Iter-156A.4 — bottom "Add character" button removed.
                    The action now lives at the top of the page next to
                    Bulk Edit and Merge so it's visible without scrolling
                    through the full cast list. (See cast-hub-curate-row
                    above.) */}
            </main>

            {/* Owner-only delete confirmation. We pre-fetch usage counts so
                the user knows EXACTLY what will move to Unassigned before
                confirming — never silently dump media. */}
            <Dialog
                open={Boolean(deleteTarget)}
                onOpenChange={(v) => !v && cancelDelete()}
            >
                <DialogContent
                    data-testid="cast-delete-dialog"
                    className="max-w-sm w-full bg-card border-white/10 p-5"
                >
                    <DialogHeader className="space-y-1 pr-8">
                        <DialogTitle className="font-display text-lg font-bold text-white">
                            Delete cast member?
                        </DialogTitle>
                        <DialogDescription className="text-xs text-neutral-400">
                            {deleteTarget ? (
                                <>
                                    <span className="text-white font-medium">
                                        {deleteTarget.name}
                                    </span>
                                    {deleteTarget.actor ? ` · ${deleteTarget.actor}` : null}
                                </>
                            ) : null}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                        {deleteLoading ? (
                            <span
                                data-testid="cast-delete-usage-loading"
                                className="inline-flex items-center gap-2"
                            >
                                <Loader2 size={12} className="animate-spin" /> Looking up where this cast member appears…
                            </span>
                        ) : deleteUsage ? (
                            <div data-testid="cast-delete-usage" className="space-y-1">
                                <p>
                                    <span className="font-semibold">
                                        {deleteUsage.state_entries}
                                    </span>{" "}
                                    image
                                    {deleteUsage.state_entries === 1 ? "" : "s"} will move to{" "}
                                    <span className="font-semibold">Unassigned</span>.
                                    Photos are not deleted.
                                </p>
                                {deleteUsage.scene_links > 0 ? (
                                    <p className="text-amber-100/80">
                                        {deleteUsage.scene_links} scene attachment
                                        {deleteUsage.scene_links === 1 ? "" : "s"} will be
                                        removed.
                                    </p>
                                ) : null}
                                <p className="text-amber-100/80">This cannot be undone.</p>
                            </div>
                        ) : null}
                    </div>

                    <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <Button
                            type="button"
                            data-testid="cast-delete-cancel"
                            variant="secondary"
                            onClick={cancelDelete}
                            disabled={deleting}
                            className="min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="cast-delete-confirm"
                            onClick={confirmDelete}
                            disabled={deleting || deleteLoading}
                            className="min-tap bg-red-500 hover:bg-red-500/90 text-white"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 size={14} className="mr-2 animate-spin" />
                                    Deleting…
                                </>
                            ) : (
                                <>
                                    <Trash2 size={14} className="mr-2" />
                                    Delete
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Owner-only edit dialog. PATCH preserves the character UUID
                so renames automatically propagate to every reference (scene
                attachments, state entries, flags) without duplicating data. */}
            <Dialog
                open={Boolean(editTarget)}
                onOpenChange={(v) => !v && cancelEdit()}
            >
                <DialogContent
                    data-testid="cast-edit-dialog"
                    className="max-w-sm w-full bg-card border-white/10 p-5"
                >
                    <DialogHeader className="space-y-1 pr-8">
                        <DialogTitle className="font-display text-lg font-bold text-white">
                            Edit cast member
                        </DialogTitle>
                        <DialogDescription className="text-xs text-neutral-400">
                            Renames apply everywhere this character is used —
                            scenes, captures, flags. No duplicates created.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-4 space-y-3">
                        {editError ? (
                            <p
                                data-testid="cast-edit-error"
                                className="text-sm text-red-400"
                            >
                                {editError}
                            </p>
                        ) : null}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-1">
                                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                    Number
                                </Label>
                                <Input
                                    data-testid="cast-edit-number-input"
                                    value={editNumber}
                                    onChange={(e) => setEditNumber(e.target.value)}
                                    placeholder="258"
                                    maxLength={40}
                                    className="mt-1.5 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                    Name · required
                                </Label>
                                <Input
                                    data-testid="cast-edit-name-input"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    placeholder="Character name"
                                    maxLength={80}
                                    autoFocus
                                    className="mt-1.5 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Actor · optional
                            </Label>
                            <Input
                                data-testid="cast-edit-actor-input"
                                value={editActor}
                                onChange={(e) => setEditActor(e.target.value)}
                                placeholder="Actor / performer"
                                maxLength={80}
                                className="mt-1.5 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                        </div>
                        {/* Team classification toggle — owner-only mirror
                            of the Add-cast dialog. Switching here PATCHes
                            the SAME character record (no duplicate is
                            created) and the new value flows through to
                            the All / Main / Crowd filters and the chip
                            colour on every other surface. */}
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Team
                            </Label>
                            <div
                                data-testid="cast-edit-team-toggle"
                                role="tablist"
                                aria-label="Character team"
                                className="mt-1.5 grid grid-cols-2 p-1 bg-secondary rounded-lg gap-1"
                            >
                                {[
                                    { k: "main", label: "Main", icon: UserRound },
                                    { k: "crowd", label: "Crowd", icon: Users },
                                ].map(({ k, label, icon: Icon }) => (
                                    <button
                                        key={k}
                                        type="button"
                                        role="tab"
                                        data-testid={`cast-edit-team-${k}`}
                                        aria-selected={editTeam === k}
                                        onClick={() => setEditTeam(k)}
                                        className={`tap h-10 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                                            editTeam === k
                                                ? "bg-primary text-primary-foreground shadow-sm"
                                                : "text-neutral-300 hover:text-white"
                                        }`}
                                    >
                                        <Icon size={14} />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <Button
                            type="button"
                            data-testid="cast-edit-cancel"
                            variant="secondary"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            className="min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="cast-edit-save"
                            onClick={saveEdit}
                            disabled={savingEdit || !editName.trim()}
                            className="min-tap font-semibold"
                        >
                            {savingEdit ? (
                                <>
                                    <Loader2 size={14} className="mr-2 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reusable Add-cast dialog. Same component is used by
                OnSetMode so the form looks identical wherever the user
                opens it. */}
            <CreateCharacterDialog
                open={createOpen}
                projectId={projectId}
                onClose={() => setCreateOpen(false)}
                onCreated={(data) => setCharacters((list) => [...list, data])}
            />

            {/* iter-103 PART 2 + 3 + 4 — Merge duplicate characters. */}
            <CharacterMergeDialog
                open={mergeOpen}
                onOpenChange={(v) => {
                    setMergeOpen(v);
                    if (!v) setMergePreset(null);
                }}
                projectId={projectId}
                characters={characters}
                presetPrimaryId={mergePreset?.presetPrimaryId || null}
                presetSecondaryIds={mergePreset?.presetSecondaryIds || null}
                presetFinalName={mergePreset?.presetFinalName || ""}
                presetMode={mergePreset ? "manual" : null}
                onMerged={async () => {
                    try {
                        const params = episode ? { episode } : {};
                        const { data } = await api.get(
                            `${apiBase}/${projectId}/characters`,
                            { params },
                        );
                        setCharacters(data);
                    } catch {
                        /* non-fatal; dialog has its own error surface */
                    }
                    // Iter-132 — refresh alias suggestions after a merge
                    // so the just-merged pair drops off the card.
                    setRetryKey((k) => k + 1);
                }}
            />
        </div>
    );
}
